import { useMemo } from 'react';
import { Empty, Modal, Segmented, Spin, Tabs, Tag, Typography, theme } from 'antd';

import {
	bucketComingItems,
	groupByArea,
	groupShippedByRelease,
	itemMetaParts,
	pendingDeployments,
} from './grouping';
import { useResolvedTab } from './useResolvedTab';
import type {
	ComingBucket,
	PendingDeployment,
	Release,
	RoadmapItem,
} from './types';

const { Paragraph, Text } = Typography;

export type UpdatesTab = 'coming' | 'new';

/** Areas map onto antd's preset tag colors so the window needs no palette. */
const AREA_COLORS: Record<string, string> = {
	Scheduling: 'blue',
	Material: 'gold',
	Quality: 'green',
	'Kit Tracking': 'purple',
	Tablet: 'cyan',
	Admin: 'default',
	Platform: 'default',
};

const BUCKET_DOTS: Record<string, string> = {
	in_progress: '#1677ff',
	up_next: 'rgba(0,0,0,0.35)',
	exploring: '#b37feb',
};

interface Props {
	open: boolean;
	onClose: () => void;
	items: RoadmapItem[];
	isLoading?: boolean;
	/** Force a tab. Omitted, the window lands wherever there is content. */
	initialTab?: UpdatesTab;
	/** Touch density for the tablet portal. */
	touch?: boolean;
}

function formatReleaseDate(value: string | null): string {
	if (!value) return '';
	const parsed = new Date(`${value}T00:00:00`);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});
}

function ItemCard({ item, touch }: { item: RoadmapItem; touch?: boolean }) {
	const { token } = theme.useToken();
	const meta = useMemo(
		() => itemMetaParts(item, (value) => formatReleaseDate(value)),
		[item],
	);

	return (
		<div
			style={{
				background: token.colorBgContainer,
				border: `1px solid ${token.colorBorderSecondary}`,
				borderRadius: token.borderRadius,
				padding: touch ? 16 : 12,
				display: 'flex',
				flexDirection: 'column',
				gap: touch ? 8 : 6,
			}}
		>
			<Tag
				color={AREA_COLORS[item.area] ?? 'default'}
				style={{ alignSelf: 'flex-start', marginInlineEnd: 0 }}
			>
				{item.area}
			</Tag>
			<Text strong style={{ fontSize: touch ? 17 : 14 }}>
				{item.title}
			</Text>
			{item.summary && (
				<Text type="secondary" style={{ fontSize: touch ? 14 : 12 }}>
					{item.summary}
				</Text>
			)}
			{meta.length > 0 && (
				<div
					style={{
						borderTop: `1px solid ${token.colorBorderSecondary}`,
						paddingTop: touch ? 8 : 6,
						marginTop: touch ? 2 : 0,
					}}
				>
					<Text
						type="secondary"
						style={{ fontSize: touch ? 13 : 11 }}
						title={meta.join(' · ')}
					>
						{meta.join(' · ')}
					</Text>
				</div>
			)}
		</div>
	);
}

function BucketColumn({
	bucket,
	touch,
}: {
	bucket: ComingBucket;
	touch?: boolean;
}) {
	const { token } = theme.useToken();

	return (
		<div
			style={{
				background: touch ? 'transparent' : token.colorFillQuaternary,
				border: touch ? 'none' : `1px solid ${token.colorFillSecondary}`,
				borderRadius: token.borderRadius,
				padding: touch ? 0 : 12,
				display: 'flex',
				flexDirection: 'column',
				gap: 10,
				minHeight: 0,
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				<span
					style={{
						width: touch ? 9 : 8,
						height: touch ? 9 : 8,
						borderRadius: '50%',
						background: BUCKET_DOTS[bucket.status],
						flexShrink: 0,
					}}
				/>
				<Text strong style={{ fontSize: touch ? 16 : 13 }}>
					{bucket.label}
				</Text>
				<Text type="secondary" style={{ fontSize: touch ? 14 : 12 }}>
					{bucket.items.length}
				</Text>
			</div>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: touch
						? 'repeat(2, minmax(0, 1fr))'
						: 'minmax(0, 1fr)',
					gap: touch ? 14 : 10,
				}}
			>
				{bucket.items.map((item) => (
					<ItemCard key={item.id} item={item} touch={touch} />
				))}
			</div>
		</div>
	);
}

function DeployBand({
	deployment,
	touch,
}: {
	deployment: PendingDeployment;
	touch?: boolean;
}) {
	const { token } = theme.useToken();
	const when = deployment.planned_date
		? `Deploying ${formatReleaseDate(deployment.planned_date)}`
		: 'Deploying soon';
	const count = deployment.items.length;

	return (
		<div
			style={{
				background: token.colorPrimaryBg,
				border: `1px solid ${token.colorPrimaryBorder ?? token.colorBorder}`,
				borderRadius: token.borderRadius,
				padding: touch ? 16 : 12,
				display: 'flex',
				flexDirection: 'column',
				gap: touch ? 14 : 10,
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
				<svg
					width={touch ? 18 : 15}
					height={touch ? 18 : 15}
					viewBox="0 0 24 24"
					fill="none"
					aria-hidden="true"
					style={{ flexShrink: 0 }}
				>
					<path
						d="M12 19V6M12 6 6.5 11.5M12 6l5.5 5.5"
						stroke={token.colorPrimary}
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M5 21h14"
						stroke={token.colorPrimary}
						strokeWidth="1.8"
						strokeLinecap="round"
					/>
				</svg>
				<Text strong style={{ fontSize: touch ? 17 : 14, color: token.colorPrimary }}>
					{when}
				</Text>
				<Text type="secondary" style={{ fontSize: touch ? 14 : 12 }}>
					{deployment.name} &middot; {count} {count === 1 ? 'change' : 'changes'}
				</Text>
			</div>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: touch
						? 'repeat(2, minmax(0, 1fr))'
						: 'repeat(3, minmax(0, 1fr))',
					gap: touch ? 14 : 10,
				}}
			>
				{deployment.items.map((item) => (
					<ItemCard key={item.id} item={item} touch={touch} />
				))}
			</div>
		</div>
	);
}

function ReleaseBlock({ release }: { release: Release }) {
	const { token } = theme.useToken();
	const areas = useMemo(() => groupByArea(release.items), [release.items]);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
			<div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
				<Text strong style={{ fontSize: 16 }}>
					v{release.version}
				</Text>
				<Text type="secondary" style={{ fontSize: 12 }}>
					{formatReleaseDate(release.released_on)}
				</Text>
			</div>

			{areas.map((group) => (
				<div
					key={group.area}
					style={{ display: 'flex', flexDirection: 'column', gap: 5 }}
				>
					<Text strong style={{ fontSize: 13, color: token.colorPrimary }}>
						{group.area}
					</Text>
					{group.items.map((item) => (
						<div key={item.id} style={{ display: 'flex', gap: 9 }}>
							<span style={{ color: token.colorTextQuaternary, flexShrink: 0 }}>
								&bull;
							</span>
							<Text style={{ fontSize: 13 }}>
								{item.summary || item.title}
							</Text>
						</div>
					))}
				</div>
			))}
		</div>
	);
}

export default function UpdatesModal({
	open,
	onClose,
	items,
	isLoading = false,
	initialTab,
	touch = false,
}: Props) {
	const { token } = theme.useToken();
	// Resolved against data that lands after the window opens — see the hook.
	const { tab: activeTab, setTab: setActiveTab } = useResolvedTab(
		open,
		items,
		initialTab,
	);

	const buckets = useMemo(() => bucketComingItems(items), [items]);
	const releases = useMemo(() => groupShippedByRelease(items), [items]);
	const deploys = useMemo(() => pendingDeployments(items), [items]);

	const hasComing =
		deploys.length > 0 || buckets.some((bucket) => bucket.items.length > 0);

	const comingPanel = (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			<Paragraph type="secondary" style={{ margin: 0, maxWidth: 760 }}>
				What the team is building next. Dates are targets, not commitments
				&mdash; the order changes as priorities move.
			</Paragraph>

			{deploys.map((deployment) => (
				<DeployBand
					key={deployment.name}
					deployment={deployment}
					touch={touch}
				/>
			))}

			{hasComing ? (
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: touch
							? 'minmax(0, 1fr)'
							: 'repeat(3, minmax(0, 1fr))',
						gap: 16,
						alignItems: 'start',
					}}
				>
					{buckets.map((bucket) => (
						<BucketColumn key={bucket.status} bucket={bucket} touch={touch} />
					))}
				</div>
			) : (
				<Empty description="Nothing queued up right now." />
			)}
		</div>
	);

	const newPanel = (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			<Paragraph type="secondary" style={{ margin: 0, maxWidth: 760 }}>
				Everything that has shipped, newest first.
			</Paragraph>

			{releases.length > 0 ? (
				releases.map((release) => (
					<ReleaseBlock key={release.version} release={release} />
				))
			) : (
				<Empty description="No releases recorded yet." />
			)}
		</div>
	);

	const body = isLoading ? (
		<div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
			<Spin />
		</div>
	) : activeTab === 'coming' ? (
		comingPanel
	) : (
		newPanel
	);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title="Fastlane Updates"
			width="min(1100px, 95vw)"
			centered
			destroyOnClose
			styles={{
				body: {
					height: '70vh',
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
					paddingTop: 0,
				},
			}}
		>
			<div style={{ flexShrink: 0 }}>
				{touch ? (
					<Segmented
						size="large"
						value={activeTab}
						onChange={(value) => setActiveTab(value as UpdatesTab)}
						options={[
							{ label: "What's Coming", value: 'coming' },
							{ label: "What's New", value: 'new' },
						]}
						style={{ marginBottom: 16 }}
					/>
				) : (
					<Tabs
						activeKey={activeTab}
						onChange={(key) => setActiveTab(key as UpdatesTab)}
						items={[
							{ key: 'coming', label: "What's Coming" },
							{ key: 'new', label: "What's New" },
						]}
					/>
				)}
			</div>

			<div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{body}</div>

			<div
				style={{
					flexShrink: 0,
					borderTop: `1px solid ${token.colorBorderSecondary}`,
					paddingTop: 12,
					marginTop: 12,
				}}
			>
				<Text type="secondary" style={{ fontSize: touch ? 14 : 12 }}>
					Got an idea, or think something here is in the wrong order? Tell the
					Fastlane team &mdash; this list is built from what people ask for.
				</Text>
			</div>
		</Modal>
	);
}
